import React from 'react';

/**
 * v2 status pills + pipeline stage tags. Pill radius 6; ink/bg/border status
 * triplets; stages use the single-hue blue ramp (never rainbow).
 */
export type PillStatus = 'success' | 'warn' | 'danger' | 'neutral';

const statusClasses: Record<PillStatus, string> = {
  success: 'bg-success-bg border-success-border text-success-ink',
  warn: 'bg-warn-bg border-warn-border text-warn-ink',
  danger: 'bg-danger-bg border-danger-border text-danger-ink',
  neutral: 'bg-neutral-bg border-neutral-border text-neutral-ink',
};

export interface PillProps {
  status?: PillStatus;
  dot?: boolean;
  /** Hover text, e.g. what the status means for the buyer. */
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const Pill: React.FC<PillProps> = ({ status = 'neutral', dot, title, children, className = '' }) => (
  <span
    title={title}
    className={`inline-flex items-center gap-s1 rounded-pill border px-s2 py-[2px] text-meta font-medium ${statusClasses[status]} ${className}`}
  >
    {dot && <span className="h-[6px] w-[6px] rounded-round bg-current" />}
    {children}
  </span>
);

/** Pipeline stage tag — stage 1..5 walks the blue ramp. */
export interface StagePillProps {
  stage: 1 | 2 | 3 | 4 | 5;
  children: React.ReactNode;
  className?: string;
}

const stageClasses: Record<StagePillProps['stage'], string> = {
  1: 'bg-pipeline-1 text-action-deep',
  2: 'bg-pipeline-2 text-action-deep',
  3: 'bg-pipeline-3 text-white',
  4: 'bg-pipeline-4 text-white',
  5: 'bg-pipeline-5 text-white',
};

export const StagePill: React.FC<StagePillProps> = ({ stage, children, className = '' }) => (
  <span
    className={`inline-flex items-center gap-s1 rounded-pill px-s2 py-[2px] text-meta font-semibold ${stageClasses[stage]} ${className}`}
  >
    {children}
  </span>
);

export default Pill;
